import {WorkspaceMembership, workspaceRoleCanProduce, WorkspaceRole} from "@/lib/types";

export const WORKSPACE_MEMBERSHIPS_CACHE_PREFIX = "braiv:lastKnownWorkspaces:";
const WORKSPACE_LIST_RETRY_ATTEMPTS = 3;

export function getWorkspaceMembershipsCacheKey(uid: string): string {
    return `${WORKSPACE_MEMBERSHIPS_CACHE_PREFIX}${uid}`;
}

function isCachedMembership(value: unknown): value is WorkspaceMembership {
    if (!value || typeof value !== "object") return false;
    const workspace = (value as WorkspaceMembership).workspace;
    return Boolean(workspace && typeof workspace.id === "string" && workspace.id);
}

export function parseCachedWorkspaceMemberships(raw: string | null): WorkspaceMembership[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isCachedMembership);
    } catch {
        return [];
    }
}

export function readCachedWorkspaceMemberships(uid: string): WorkspaceMembership[] {
    if (typeof window === "undefined" || !uid) return [];
    try {
        return parseCachedWorkspaceMemberships(
            window.localStorage.getItem(getWorkspaceMembershipsCacheKey(uid)),
        );
    } catch {
        return [];
    }
}

export function writeCachedWorkspaceMemberships(uid: string, memberships: WorkspaceMembership[]) {
    if (typeof window === "undefined" || !uid) return;
    try {
        window.localStorage.setItem(
            getWorkspaceMembershipsCacheKey(uid),
            JSON.stringify(memberships),
        );
    } catch (error) {
        console.warn("Failed to cache workspace memberships:", error);
    }
}

export function shouldClearStaleActiveWorkspace(input: {
    fetchSucceeded: boolean;
    currentWorkspaceId: string | null;
    membershipIds: Array<string | undefined>;
}): boolean {
    if (!input.fetchSucceeded || !input.currentWorkspaceId) return false;
    return !input.membershipIds.includes(input.currentWorkspaceId);
}

export function nextWorkspaceListRetryDelayMs(attemptIndex: number): number | null {
    if (attemptIndex < 0 || attemptIndex >= WORKSPACE_LIST_RETRY_ATTEMPTS) return null;
    return 400 * (attemptIndex + 1);
}

export function workspaceSwitcherPresentation(input: {
    activeWorkspaceId: string | null;
    activeWorkspaceName?: string | null;
    workspacesLoading: boolean;
}): {label: string; kind: "personal" | "workspace" | "loading"} {
    if (input.activeWorkspaceName) {
        return {label: input.activeWorkspaceName, kind: "workspace"};
    }
    if (input.activeWorkspaceId) {
        return input.workspacesLoading ?
            {label: "Loading…", kind: "loading"} :
            {label: "Workspace", kind: "workspace"};
    }
    return {label: "Personal", kind: "personal"};
}

export function canProduceInActiveContext(input: {
    isTeamContext: boolean;
    role?: WorkspaceRole | null;
    workspacesLoading: boolean;
    hasResolvedMembership: boolean;
}): boolean {
    if (!input.isTeamContext) return true;
    if (input.role) return workspaceRoleCanProduce(input.role);
    // Persisted team context without a loaded membership is not a role denial.
    // Stay optimistic until the list resolves so cold-load does not look like
    // "member cannot produce".
    return input.workspacesLoading || !input.hasResolvedMembership;
}

export function shouldOfferPersonalContext(accountModel: string | null | undefined): boolean {
    return accountModel !== "workspace_native";
}

/** Persisted team selection wins. A native account with nothing stored opens on its home workspace. */
export function resolveActiveWorkspaceId(input: {
    persistedWorkspaceId: string | null | undefined;
    accountModel: string | null | undefined;
    homeWorkspaceId: string | null | undefined;
}): string | null {
    const persisted = typeof input.persistedWorkspaceId === "string" ? input.persistedWorkspaceId.trim() : "";
    if (persisted) return persisted;
    if (input.accountModel !== "workspace_native") return null;
    const home = typeof input.homeWorkspaceId === "string" ? input.homeWorkspaceId.trim() : "";
    return home || null;
}

export function shouldHideNativeHomeChrome(input: {
    accountModel: string | null | undefined;
    homeWorkspaceId: string | null | undefined;
    membershipWorkspaceIds: Array<string | undefined>;
}): boolean {
    if (input.accountModel !== "workspace_native" || !input.homeWorkspaceId) {
        return false;
    }
    return !input.membershipWorkspaceIds.some((id) => id && id !== input.homeWorkspaceId);
}

export function storageAnchorForWorkspace(
    memberships: WorkspaceMembership[],
    workspaceId: string | null | undefined,
): string | undefined {
    if (!workspaceId) return undefined;
    const membership = memberships.find((item) => item.workspace.id === workspaceId);
    const anchor = membership?.workspace.storageAnchorUid;
    return typeof anchor === "string" && anchor.length > 0 ? anchor : undefined;
}

export async function resolveWorkspaceUploadScope(input: {
    workspaceId: string | null;
    storageAnchorUid?: string | null;
    refreshWorkspaces: () => Promise<WorkspaceMembership[]>;
}): Promise<{workspaceId?: string; storageAnchorUid?: string} | null> {
    if (!input.workspaceId) {
        return {};
    }
    if (input.storageAnchorUid) {
        return {
            workspaceId: input.workspaceId,
            storageAnchorUid: input.storageAnchorUid,
        };
    }
    const memberships = await input.refreshWorkspaces();
    const storageAnchorUid = storageAnchorForWorkspace(memberships, input.workspaceId);
    if (!storageAnchorUid) {
        return null;
    }
    return {workspaceId: input.workspaceId, storageAnchorUid};
}
